import React, { useEffect, useRef } from 'react';

interface EQVisualizerProps {
  lowFreq: number;
  lowGain: number;
  lowQ: number;
  midFreq: number;
  midGain: number;
  midQ: number;
  highFreq: number;
  highGain: number;
  highQ: number;
}

/**
 * EQ Visualizer Component
 * Renders frequency response curve using Canvas
 * Shows frequency on X-axis (20Hz - 20kHz, log scale)
 * Shows gain on Y-axis (-24dB to +24dB)
 */
export const EQVisualizer: React.FC<EQVisualizerProps> = ({
  lowFreq,
  lowGain,
  lowQ,
  midFreq,
  midGain,
  midQ,
  highFreq,
  highGain,
  highQ,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas size
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Draw background
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, width, height);
    
    // Draw grid
    drawGrid(ctx, width, height);
    
    // Draw frequency response curve
    drawFrequencyResponse(ctx, width, height, {
      lowFreq,
      lowGain,
      lowQ,
      midFreq,
      midGain,
      midQ,
      highFreq,
      highGain,
      highQ,
    });
  }, [lowFreq, lowGain, lowQ, midFreq, midGain, midQ, highFreq, highGain, highQ]);
  
  return (
    <div className="eq-visualizer">
      <canvas
        ref={canvasRef}
        width={300}
        height={150}
        style={{
          width: '100%',
          height: 'auto',
          borderRadius: '4px',
        }}
      />
    </div>
  );
};

/**
 * Draw grid lines and labels
 */
function drawGrid(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  ctx.strokeStyle = '#333';
  ctx.lineWidth = 1;
  ctx.font = '10px monospace';
  ctx.fillStyle = '#666';
  
  // Draw horizontal grid lines (gain levels)
  const gainLevels = [-24, -12, 0, 12, 24];
  gainLevels.forEach((gain) => {
    const y = gainToY(gain, height);
    
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
    
    // Draw label
    ctx.fillText(`${gain}dB`, 5, y - 3);
  });
  
  // Draw vertical grid lines (frequency markers)
  const freqMarkers = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
  freqMarkers.forEach((freq) => {
    const x = freqToX(freq, width);
    
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
    
    // Draw label
    const label = freq >= 1000 ? `${freq / 1000}k` : `${freq}`;
    ctx.fillText(label, x - 10, height - 5);
  });
  
  // Draw center line (0dB) thicker
  ctx.strokeStyle = '#555';
  ctx.lineWidth = 2;
  const centerY = gainToY(0, height);
  ctx.beginPath();
  ctx.moveTo(0, centerY);
  ctx.lineTo(width, centerY);
  ctx.stroke();
}

/**
 * Draw frequency response curve
 */
function drawFrequencyResponse(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  params: {
    lowFreq: number;
    lowGain: number;
    lowQ: number;
    midFreq: number;
    midGain: number;
    midQ: number;
    highFreq: number;
    highGain: number;
    highQ: number;
  }
): void {
  const sampleRate = 48000;
  
  // Calculate coefficients for all three bands
  const lowCoeffs = calculatePeakingCoefficients(
    params.lowFreq,
    params.lowGain,
    params.lowQ,
    sampleRate
  );
  const midCoeffs = calculatePeakingCoefficients(
    params.midFreq,
    params.midGain,
    params.midQ,
    sampleRate
  );
  const highCoeffs = calculatePeakingCoefficients(
    params.highFreq,
    params.highGain,
    params.highQ,
    sampleRate
  );
  
  // Draw curve
  ctx.strokeStyle = '#4CAF50';
  ctx.lineWidth = 2;
  ctx.beginPath();
  
  let firstPoint = true;
  
  // Sample frequency response at many points
  for (let x = 0; x < width; x++) {
    const freq = xToFreq(x, width);
    
    // Calculate combined frequency response
    const lowResponse = calculateFrequencyResponse(lowCoeffs, freq, sampleRate);
    const midResponse = calculateFrequencyResponse(midCoeffs, freq, sampleRate);
    const highResponse = calculateFrequencyResponse(highCoeffs, freq, sampleRate);
    
    // Combine responses (multiply magnitudes in linear domain, add in dB domain)
    const combinedGain = lowResponse + midResponse + highResponse;
    
    const y = gainToY(combinedGain, height);
    
    if (firstPoint) {
      ctx.moveTo(x, y);
      firstPoint = false;
    } else {
      ctx.lineTo(x, y);
    }
  }
  
  ctx.stroke();
  
  // Draw band markers
  drawBandMarker(ctx, width, height, params.lowFreq, params.lowGain, '#FF5722');
  drawBandMarker(ctx, width, height, params.midFreq, params.midGain, '#2196F3');
  drawBandMarker(ctx, width, height, params.highFreq, params.highGain, '#FFC107');
}

/**
 * Draw a marker for a specific band
 */
function drawBandMarker(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  freq: number,
  gain: number,
  color: string
): void {
  const x = freqToX(freq, width);
  const y = gainToY(gain, height);
  
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, 4, 0, 2 * Math.PI);
  ctx.fill();
}

/**
 * Convert frequency to X coordinate (logarithmic scale)
 */
function freqToX(freq: number, width: number): number {
  const minFreq = 20;
  const maxFreq = 20000;
  
  // Logarithmic scale
  const logMin = Math.log10(minFreq);
  const logMax = Math.log10(maxFreq);
  const logFreq = Math.log10(freq);
  
  const normalized = (logFreq - logMin) / (logMax - logMin);
  return normalized * width;
}

/**
 * Convert X coordinate to frequency (logarithmic scale)
 */
function xToFreq(x: number, width: number): number {
  const minFreq = 20;
  const maxFreq = 20000;
  
  const normalized = x / width;
  
  // Logarithmic scale
  const logMin = Math.log10(minFreq);
  const logMax = Math.log10(maxFreq);
  const logFreq = logMin + normalized * (logMax - logMin);
  
  return Math.pow(10, logFreq);
}

/**
 * Convert gain (dB) to Y coordinate
 */
function gainToY(gain: number, height: number): number {
  const minGain = -24;
  const maxGain = 24;
  
  // Invert Y axis (0 at top, height at bottom)
  const normalized = (gain - minGain) / (maxGain - minGain);
  return height - normalized * height;
}

/**
 * Convert dB to amplitude for peaking EQ
 */
function dbToAmplitude(db: number): number {
  db = Math.max(-24.0, Math.min(24.0, db));
  const expVal = db * 0.05756462733;
  
  if (Math.abs(expVal) < 2.0) {
    const x2 = expVal * expVal;
    const x3 = x2 * expVal;
    const x4 = x3 * expVal;
    return 1.0 + expVal + x2 / 2.0 + x3 / 6.0 + x4 / 24.0;
  } else {
    return Math.pow(10, db / 40.0);
  }
}

/**
 * Calculate biquad coefficients for peaking EQ
 */
function calculatePeakingCoefficients(
  freq: number,
  gain: number,
  q: number,
  sampleRate: number
): { b0: number; b1: number; b2: number; a1: number; a2: number } {
  q = Math.max(0.1, q);
  const A = dbToAmplitude(gain);
  const omega0 = (2 * Math.PI * freq) / sampleRate;
  const sinOmega0 = Math.sin(omega0);
  const cosOmega0 = Math.cos(omega0);
  const alpha = sinOmega0 / (2 * q);
  
  const b0 = 1.0 + alpha * A;
  const b1 = -2.0 * cosOmega0;
  const b2 = 1.0 - alpha * A;
  let a0 = 1.0 + alpha / A;
  const a1 = -2.0 * cosOmega0;
  const a2 = 1.0 - alpha / A;
  
  if (Math.abs(a0) < 0.0001) {
    a0 = 1.0;
  }
  
  return {
    b0: b0 / a0,
    b1: b1 / a0,
    b2: b2 / a0,
    a1: a1 / a0,
    a2: a2 / a0,
  };
}

/**
 * Calculate frequency response of a biquad filter at a specific frequency
 * Returns gain in dB
 */
function calculateFrequencyResponse(
  coeffs: { b0: number; b1: number; b2: number; a1: number; a2: number },
  freq: number,
  sampleRate: number
): number {
  // Calculate normalized frequency (0 to π)
  const omega = (2 * Math.PI * freq) / sampleRate;
  
  // Calculate complex frequency response
  // H(e^jω) = (b0 + b1*e^-jω + b2*e^-2jω) / (1 + a1*e^-jω + a2*e^-2jω)
  
  const cosOmega = Math.cos(omega);
  const cos2Omega = Math.cos(2 * omega);
  const sinOmega = Math.sin(omega);
  const sin2Omega = Math.sin(2 * omega);
  
  // Numerator (real and imaginary parts)
  const numReal = coeffs.b0 + coeffs.b1 * cosOmega + coeffs.b2 * cos2Omega;
  const numImag = -coeffs.b1 * sinOmega - coeffs.b2 * sin2Omega;
  
  // Denominator (real and imaginary parts)
  const denReal = 1.0 + coeffs.a1 * cosOmega + coeffs.a2 * cos2Omega;
  const denImag = -coeffs.a1 * sinOmega - coeffs.a2 * sin2Omega;
  
  // Calculate magnitude
  const numMag = Math.sqrt(numReal * numReal + numImag * numImag);
  const denMag = Math.sqrt(denReal * denReal + denImag * denImag);
  
  const magnitude = numMag / (denMag + 1e-10); // Add small value to prevent division by zero
  
  // Convert to dB
  return 20 * Math.log10(magnitude + 1e-10);
}
